import { useEffect, useMemo } from "react";
import { Badge, Button, Card, Heading, Link, Text } from "@components/theming";
import { TrashIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { EventShowing, ShowingMessage } from "@types";
import { EventConfigOverrides } from "@utils/flags";
import { joinClassNames } from "@utils/helpers";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { handleShowingType } from "../utils/helpers";
import ShowingStatusToolTip from "./showingStatusToolTip";

interface Props {
  showingcomboconfig?: {
    connectedShowings: number[];
    maxShowings: number;
    minShowings: number;
    perShowingLimit: number | null;
  } | null;
  activeShowings: EventShowing[];
  basketEvent: any;
  eventConfigOverrides?: EventConfigOverrides;
  selectedConnectedShowings: number[];
  setSelectedConnectedShowings: React.Dispatch<React.SetStateAction<number[]>>;
  messages?: ShowingMessage[];
  children: React.ReactNode;
}
const selected = {
  hide: {
    height: 0,
    marginBottom: 0,
    transition: {
      delay: 0.2,
    },
  },
  show: {
    height: "auto",
    marginBottom: "1.25rem",
  },
};

const container = {
  hide: {
    opacity: 0,
  },
  show: {
    opacity: 1,
    transition: {
      delayChildren: 0.4,
      staggerChildren: 0.05,
    },
  },
};

function ConnectedShowings({
  showingcomboconfig,
  activeShowings,
  basketEvent,
  eventConfigOverrides,
  selectedConnectedShowings,
  setSelectedConnectedShowings,
  messages,
  children,
}: Props) {
  const minSelectsRemaining =
    showingcomboconfig?.connectedShowings && showingcomboconfig?.minShowings
      ? showingcomboconfig.minShowings - selectedConnectedShowings.length
      : 0;
  const maxSelectsRemaining =
    showingcomboconfig?.connectedShowings && showingcomboconfig?.maxShowings
      ? showingcomboconfig.maxShowings - selectedConnectedShowings.length
      : 0;

  const t = useTranslations("EventPage.connectedshowings");
  const statusTranslations = useTranslations("EventPage.statuses");
  const cvv = eventConfigOverrides?.cartValidationRules;

  const connectedShowings = useMemo(() => {
    const showingIdsInBasket =
      basketEvent?.Showings.flatMap((showing: any) => {
        return showing.Items.flatMap((item: any) => {
          if (item.ConnectedTickets && item.ConnectedTickets.length > 0) {
            return item.ConnectedTickets.map((ticket: any) => ticket.ShowingId);
          } else {
            return [showing.Id];
          }
        });
      }) || [];
    const showingCounts = showingIdsInBasket?.reduce(
      (counts: { [key: number]: number }, id: number) => {
        counts[id] = (counts[id] || 0) + 1;
        return counts;
      },
      {}
    );

    const basketIds = new Set<number>(showingIdsInBasket);
    const selectedIds = new Set<number>(selectedConnectedShowings);
    const idsToCheck = new Set<number>([...basketIds, ...selectedIds]);
    const unbookableConnectedShowings = [
      ...new Set([
        ...showingIdsInBasket.filter(
          (id: number) =>
            showingCounts[id] >= (showingcomboconfig?.perShowingLimit || 100)
        ),
        ...(cvv?.mutuallyExclusiveShowings
          ? cvv.mutuallyExclusiveShowings
              .filter((group) => group.some((id) => idsToCheck.has(id)))
              .flat()
              .filter((id) => {
                if (basketIds.has(id)) return true;
                if (selectedIds.has(id)) return false;
                return true;
              })
          : []),
      ]),
    ];
    return (
      showingcomboconfig?.connectedShowings.filter(
        (id) => !unbookableConnectedShowings.includes(id)
      ) || []
    );
  }, [basketEvent, cvv, selectedConnectedShowings, showingcomboconfig]);

  useEffect(() => {
    if (
      !selectedConnectedShowings.every((id) => connectedShowings.includes(id))
    ) {
      setSelectedConnectedShowings((prev) =>
        prev.filter((id) => connectedShowings.includes(id))
      );
    }
  }, [
    selectedConnectedShowings,
    setSelectedConnectedShowings,
    connectedShowings,
  ]);

  const relevantMutuallyExclusiveGroups = cvv?.mutuallyExclusiveShowings
    ? cvv.mutuallyExclusiveShowings
        .map((group) => group.filter((id) => connectedShowings.includes(id)))
        .filter((group) => group.length > 1)
    : [];
  const mutuallyExclusiveShowingIds = Array.from(
    new Set(relevantMutuallyExclusiveGroups.flat())
  );
  const mutuallyExclusiveConnectedShowings = connectedShowings.filter((id) =>
    mutuallyExclusiveShowingIds.includes(id)
  );
  const nonMutuallyExclusiveConnectedShowings = connectedShowings.filter(
    (id) => {
      return !mutuallyExclusiveShowingIds.includes(id);
    }
  );

  const amountToCheck = showingcomboconfig?.minShowings
    ? showingcomboconfig.minShowings -
      nonMutuallyExclusiveConnectedShowings.length
    : 0;

  const canFillPassMemo = new Map<string, boolean>();

  function checkCanFillPass(showings: number[], needed: number): boolean {
    if (needed <= 0) {
      return true;
    }
    if (showings.length < needed) {
      return false;
    }

    const key = `${needed}|${showings.join(",")}`;
    const cached = canFillPassMemo.get(key);
    if (cached !== undefined) {
      return cached;
    }

    const [curr, ...rest] = showings;
    const affected = new Set(
      relevantMutuallyExclusiveGroups
        .filter((group) => group.includes(curr))
        .flat()
    );

    const result =
      checkCanFillPass(
        rest.filter((id) => !affected.has(id)),
        needed - 1
      ) || checkCanFillPass(rest, needed);

    canFillPassMemo.set(key, result);
    return result;
  }

  const canFillPass = checkCanFillPass(
    mutuallyExclusiveConnectedShowings,
    amountToCheck
  );

  return (
    <div className="flex flex-col">
      {!canFillPass ? (
        <Text size="3" weight="medium">
          {t.rich("noshowingsavailable", {
            link: (chunks: React.ReactNode) => (
              <Text
                asChild
                whitelabelClasses="text-[color:var(--blue-11)] visited:text-[color:var(--purple-11)]"
                shopsiteClasses="text-blue-500 visited:text-purple-500"
              >
                <Link href="/cart">{chunks}</Link>
              </Text>
            ),
          })}
        </Text>
      ) : (
        <>
          <motion.div
            variants={selected}
            initial="hide"
            animate={selectedConnectedShowings.length > 0 ? "show" : "hide"}
            className="flex flex-col gap-2"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={
                selectedConnectedShowings.length > 0
                  ? { opacity: 1, transition: { delay: 0.2 } }
                  : { opacity: 0 }
              }
              className="flex items-center justify-between"
            >
              <Heading
                as="h2"
                weight="bold"
                size="4"
                shopsiteClasses="text-lg font-semibold text-black truncate"
              >
                {showingcomboconfig?.connectedShowings.length !==
                showingcomboconfig?.minShowings
                  ? t("selectedshowings")
                  : t("selectedshowingsnochoice")}
              </Heading>
              {showingcomboconfig?.connectedShowings.length !==
                showingcomboconfig?.minShowings && (
                <AnimatePresence>
                  <Button
                    color="red"
                    whitelabelVariant="soft"
                    onClick={() => setSelectedConnectedShowings([])}
                    shopsiteClasses="gap-1 py-1 !rounded-lg bg-red-100 !text-red-800 hover:bg-red-500 hover:!text-white"
                  >
                    <TrashIcon className="h-5 w-5" />
                    <Text
                      as="span"
                      weight="medium"
                      shopsiteClasses="font-semibold text-base"
                    >
                      {t("clear")}
                    </Text>
                  </Button>
                </AnimatePresence>
              )}
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={
                selectedConnectedShowings.length > 0
                  ? { opacity: 1, transition: { delay: 0.2 } }
                  : { opacity: 0 }
              }
              className="flex items-center gap-1 flex-wrap"
            >
              {connectedShowings.map((showing_id) => {
                const connectedShowing = activeShowings.find(
                  (showing) => showing.id === showing_id
                );
                if (!connectedShowing) {
                  return null;
                }
                return (
                  <AnimatePresence key={showing_id}>
                    {selectedConnectedShowings.includes(showing_id) && (
                      <Badge
                        asChild
                        size="3"
                        shopsiteClasses="!rounded-lg !py-1"
                      >
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          <Text as="span" shopsiteClasses="text-sm font-medium">
                            {connectedShowing.name ||
                              connectedShowing.date.display}
                          </Text>
                          {showingcomboconfig?.connectedShowings.length !==
                          showingcomboconfig?.minShowings ? (
                            <button
                              onClick={() => {
                                setSelectedConnectedShowings((prev) =>
                                  prev.filter((id) => id !== showing_id)
                                );
                              }}
                            >
                              <XMarkIcon className="h-5 w-5" />
                            </button>
                          ) : null}
                        </motion.div>
                      </Badge>
                    )}
                  </AnimatePresence>
                );
              })}
            </motion.div>
          </motion.div>
          <motion.div layout="position">
            <Heading
              as="h2"
              asChild
              size="3"
              shopsiteClasses="text-lg font-semibold"
              sharedClasses="truncate"
            >
              <motion.h2
                initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                animate={
                  maxSelectsRemaining > 0
                    ? {
                        opacity: 1,
                        height: "auto",
                        marginBottom: "1.25rem",
                        transition: { opacity: { delay: 0.2 } },
                      }
                    : {
                        opacity: 0,
                        height: 0,
                        marginBottom: 0,
                        transition: {
                          height: { delay: 0.2 },
                          marginBottom: { delay: 0.2 },
                        },
                      }
                }
                transition={{ delay: 0.2 }}
              >
                {t("chooseshowing", {
                  maxRemaining: Math.max(maxSelectsRemaining, 1),
                  numShowings: selectedConnectedShowings.length,
                })}
              </motion.h2>
            </Heading>
            <AnimatePresence>
              {maxSelectsRemaining > 0 && (
                <motion.div
                  initial={{ opacity: 1, height: "auto" }}
                  animate={{
                    opacity: 1,
                    height: "auto",
                    marginBottom: minSelectsRemaining > 0 ? 0 : "2rem",
                  }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <motion.div
                    variants={container}
                    initial="hide"
                    animate="show"
                    className="isolate grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6"
                  >
                    {connectedShowings.map((showingId) => {
                      const connectedShowing = activeShowings.find(
                        (showing) => showing.id === showingId
                      );
                      if (!connectedShowing) {
                        return null;
                      }
                      const statusInfo = handleShowingType(
                        connectedShowing,
                        messages,
                        statusTranslations
                      );
                      return (
                        <Card
                          asChild
                          key={showingId}
                          sharedClasses={joinClassNames(
                            maxSelectsRemaining === 0 ||
                              selectedConnectedShowings.includes(showingId)
                              ? "hidden"
                              : "block",
                            statusInfo.selectable
                              ? "cursor-pointer"
                              : "cursor-default",
                            "group"
                          )}
                          shopsiteClasses="relative rounded-md border shadow border-gray-100 transition-colors ease-in-out duration-200 px-4 py-2 disabled:bg-gray-100"
                          whitelabelClasses="group disabled:bg-[color:var(--gray-a2)] disabled:before:hidden disabled:after:hidden"
                        >
                          <motion.button
                            variants={{
                              hide: {
                                opacity: 0,
                                x: 5,
                              },
                              show: {
                                opacity: statusInfo.selectable ? 1 : 0.8,
                                x: 0,
                              },
                            }}
                            onClick={() =>
                              setSelectedConnectedShowings((prev) =>
                                !prev.includes(showingId) &&
                                minSelectsRemaining > 0
                                  ? [...prev, showingId]
                                  : prev.includes(showingId)
                                  ? prev.filter((id) => id !== showingId)
                                  : prev
                              )
                            }
                            disabled={!statusInfo.selectable}
                          >
                            <div className="flex items-center justify-between gap-2 md:max-lg:flex-col md:max-lg:items-start">
                              <div className="flex flex-col overflow-hidden justify-center text-base font-medium w-full group-disabled:opacity-80">
                                <Text
                                  as="p"
                                  weight="medium"
                                  sharedClasses="text-left truncate"
                                  shopsiteClasses="group-disabled:text-gray-500"
                                >
                                  {connectedShowing?.name}
                                </Text>
                                <Text
                                  as="span"
                                  weight="medium"
                                  sharedClasses="text-left text-sm w-fit"
                                  whitelabelClasses="text-[color:var(--accent-11)]"
                                  shopsiteClasses="text-indigo-500"
                                >
                                  {connectedShowing?.date.display
                                    ? connectedShowing.date.display
                                    : connectedShowing?.date.local.formatted}
                                </Text>
                              </div>
                              {!statusInfo.selectable && statusInfo.title && (
                                <Badge
                                  size="3"
                                  shopsiteClasses="gap-1 !py-1.5 !rounded-md whitespace-nowrap font-medium"
                                  color={
                                    statusInfo.color as
                                      | "red"
                                      | "green"
                                      | "orange"
                                      | undefined
                                  }
                                >
                                  <Text
                                    as="span"
                                    shopsiteClasses={statusInfo.textColor}
                                  >
                                    {statusInfo.title}
                                  </Text>
                                  {statusInfo.description?.trim() ? (
                                    <ShowingStatusToolTip
                                      statusId={connectedShowing.status.id}
                                      accessType={connectedShowing.access.type}
                                      title={statusInfo.title ?? ""}
                                      description={statusInfo.description ?? ""}
                                    />
                                  ) : (
                                    <></>
                                  )}
                                </Badge>
                              )}
                            </div>
                          </motion.button>
                        </Card>
                      );
                    })}
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
            <AnimatePresence>
              {minSelectsRemaining === 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  {children}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </>
      )}
    </div>
  );
}

export default function ConnectedShowingsWrapper({
  showingcomboconfig,
  activeShowings,
  basketEvent,
  eventConfigOverrides,
  selectedConnectedShowings,
  setSelectedConnectedShowings,
  children,
}: Props) {
  if (
    !showingcomboconfig?.connectedShowings ||
    showingcomboconfig.connectedShowings.length === 0
  ) {
    return <>{children}</>;
  }
  return (
    <ConnectedShowings
      showingcomboconfig={showingcomboconfig}
      activeShowings={activeShowings}
      basketEvent={basketEvent}
      eventConfigOverrides={eventConfigOverrides}
      selectedConnectedShowings={selectedConnectedShowings}
      setSelectedConnectedShowings={setSelectedConnectedShowings}
    >
      {children}
    </ConnectedShowings>
  );
}
